import React, { useState } from 'react';
import { TrendingUp, Award, Calendar } from 'lucide-react';
import { iccRankings, allTimeRecords, formatInfo, upcomingEvents } from '../data/cricketStats';

const StatsSection = () => {
  const [activeFormat, setActiveFormat] = useState('test');
  const [activeView, setActiveView] = useState('rankings');

  const formats = [
    { id: 'test', name: 'Test', color: 'bg-red-500' },
    { id: 'odi', name: 'ODI', color: 'bg-blue-500' },
    { id: 't20i', name: 'T20I', color: 'bg-green-500' }
  ];

  return (
    <section id="stats" className="bg-black py-24">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold text-white tracking-[0.05em] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Cricket Statistics & Rankings
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Stay updated with ICC rankings, all-time records, and upcoming events
          </p>
        </div>

        {/* View Toggle */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={() => setActiveView('rankings')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-all ${
              activeView === 'rankings' ? 'bg-white text-black' : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            <TrendingUp size={18} />
            ICC Rankings
          </button>
          <button
            onClick={() => setActiveView('records')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-all ${
              activeView === 'records' ? 'bg-white text-black' : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            <Award size={18} />
            All-Time Records
          </button>
          <button
            onClick={() => setActiveView('events')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-all ${
              activeView === 'events' ? 'bg-white text-black' : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            <Calendar size={18} />
            Upcoming Events
          </button>
        </div>

        {/* Format Selector */}
        {(activeView === 'rankings' || activeView === 'records') && (
          <div className="flex justify-center gap-4 mb-8">
            {formats.map((format) => (
              <button
                key={format.id}
                onClick={() => setActiveFormat(format.id)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  activeFormat === format.id
                    ? `${format.color} text-white`
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {format.name}
              </button>
            ))}
          </div>
        )}

        {/* Format Info */}
        {(activeView === 'rankings' || activeView === 'records') && (
          <div className="bg-gray-900 rounded-lg p-4 mb-8 text-center">
            <p className="text-gray-300 text-sm">
              <span className="text-2xl mr-2">{formatInfo[activeFormat].icon}</span>
              <span className="font-semibold">{formatInfo[activeFormat].name}</span> - {formatInfo[activeFormat].duration} | 
              First Match: {formatInfo[activeFormat].firstMatch}
            </p>
          </div>
        )}

        {/* Rankings View */}
        {activeView === 'rankings' && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Team Rankings */}
            <div className="bg-gray-900 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">Team Rankings</h3>
              <div className="space-y-3">
                {iccRankings[activeFormat].teams.slice(0, 8).map((team) => (
                  <div key={team.rank} className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        team.rank === 1 ? 'bg-yellow-500 text-black' :
                        team.rank === 2 ? 'bg-gray-400 text-black' :
                        team.rank === 3 ? 'bg-orange-600 text-white' :
                        'bg-gray-700 text-white'
                      }`}>
                        {team.rank}
                      </span>
                      <span className="text-xl">{team.flag}</span>
                      <span className="text-white font-medium">{team.team}</span>
                    </div>
                    <span className="text-gray-400 font-mono">{team.rating}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Batsmen Rankings */}
            <div className="bg-gray-900 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">Top Batsmen</h3>
              <div className="space-y-3">
                {iccRankings[activeFormat].batsmen.map((player) => (
                  <div key={player.rank} className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        player.rank === 1 ? 'bg-yellow-500 text-black' :
                        player.rank === 2 ? 'bg-gray-400 text-black' :
                        player.rank === 3 ? 'bg-orange-600 text-white' :
                        'bg-gray-700 text-white'
                      }`}>
                        {player.rank}
                      </span>
                      <div>
                        <p className="text-white font-medium text-sm">{player.name}</p>
                        <p className="text-gray-500 text-xs">{player.team}</p>
                      </div>
                    </div>
                    <span className="text-gray-400 font-mono text-sm">{player.rating}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bowlers Rankings */}
            <div className="bg-gray-900 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">Top Bowlers</h3>
              <div className="space-y-3">
                {iccRankings[activeFormat].bowlers.map((player) => (
                  <div key={player.rank} className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        player.rank === 1 ? 'bg-yellow-500 text-black' :
                        player.rank === 2 ? 'bg-gray-400 text-black' :
                        player.rank === 3 ? 'bg-orange-600 text-white' :
                        'bg-gray-700 text-white'
                      }`}>
                        {player.rank}
                      </span>
                      <div>
                        <p className="text-white font-medium text-sm">{player.name}</p>
                        <p className="text-gray-500 text-xs">{player.team}</p>
                      </div>
                    </div>
                    <span className="text-gray-400 font-mono text-sm">{player.rating}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Records View */}
        {activeView === 'records' && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Batting Records */}
            <div className="bg-gray-900 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                🏏 Batting Records
              </h3>
              <div className="space-y-4">
                {allTimeRecords[activeFormat].batting.map((record, index) => (
                  <div key={index} className="border-b border-gray-800 pb-3 last:border-0">
                    <p className="text-gray-400 text-sm">{record.record}</p>
                    <p className="text-white font-medium">{record.holder}</p>
                    <p className="text-yellow-500 font-bold">{record.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Bowling Records */}
            <div className="bg-gray-900 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                🎯 Bowling Records
              </h3>
              <div className="space-y-4">
                {allTimeRecords[activeFormat].bowling.map((record, index) => (
                  <div key={index} className="border-b border-gray-800 pb-3 last:border-0">
                    <p className="text-gray-400 text-sm">{record.record}</p>
                    <p className="text-white font-medium">{record.holder}</p>
                    <p className="text-yellow-500 font-bold">{record.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Upcoming Events */}
        {activeView === 'events' && (
          <div className="grid md:grid-cols-2 gap-6">
            {upcomingEvents.map((event, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-6 hover:from-gray-800 hover:to-gray-700 transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white">{event.name}</h3>
                    <p className="text-gray-400">{event.date}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    event.format === 'ODI' ? 'bg-blue-500/20 text-blue-400' :
                    event.format === 'T20' || event.format === 'T20I' ? 'bg-green-500/20 text-green-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {event.format}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-300">
                    <span className="text-gray-500">Venue:</span> {event.venue}
                  </p>
                  <p className="text-gray-300">
                    <span className="text-gray-500">Teams:</span> {event.teams}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Last Updated */}
        <p className="text-center text-gray-600 text-sm mt-8">
          Rankings last updated: {iccRankings.lastUpdated}
        </p>
      </div>
    </section>
  );
};

export default StatsSection;
